import { useState } from "react";
import type { ResultadoDoFechamento, SimulacaoDeFechamento } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useFecharCompetencia, useSimularFechamento } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { Confirmacao } from "@/componentes/Confirmacao";
import { PainelLateral } from "@/componentes/PainelLateral";
import { Campo } from "@/componentes/Campo";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { competenciaAnterior } from "@/calendario";
import { competencia, contagem, dia, dinheiro, idCurto, quantidade } from "@/formato";
import { campoDaRecusa, nomeDoPagador, simulacaoDesatualizada } from "./fatura";

/*
 * O fechamento da competência, em três passos: escolher o mês, confirmar os
 * números da simulação e ver o que foi gerado.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O NÚMERO CONFIRMADO É O QUE VAI SER GRAVADO (design D8).                 │
 * │                                                                          │
 * │ A confirmação mostra a simulação do servidor — e não uma conta feita     │
 * │ aqui —, e o fechamento devolve a `marca` dela. Se um estorno chegou      │
 * │ entre a leitura e o clique, o servidor recusa com                        │
 * │ `409 simulacao_desatualizada` e a simulação nova, e a confirmação        │
 * │ remonta com os números de agora: o operador decide de novo, sobre o que  │
 * │ de fato vai ser gravado.                                                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * A severidade é `critica`: são N faturas de uma vez, imutáveis no banco, e
 * o que sai daqui é cobrança. Digitar a competência é o atrito que separa
 * "cliquei" de "decidi fechar outubro".
 */
export function FechamentoDeCompetencia({ aoFechar }: { aoFechar: () => void }) {
  const { cliente } = useSessao();
  const simular = useSimularFechamento(cliente);
  const fechar = useFecharCompetencia(cliente);

  const [mes, setMes] = useState(competenciaAnterior);
  const [erroDoMes, setErroDoMes] = useState<string | undefined>(undefined);
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);
  const [simulacao, setSimulacao] = useState<SimulacaoDeFechamento | null>(null);
  const [mudou, setMudou] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDoFechamento | null>(null);

  function simularMes() {
    setErroDoMes(undefined);
    setErroGeral(undefined);
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(mes)) {
      setErroDoMes("Escolha o mês.");
      return;
    }

    simular.mutate(
      { competencia: mes },
      {
        onSuccess: (simulada) => {
          // Antes da carência a simulação responde, mas o fechamento não: a
          // tela para aqui, com a data, em vez de abrir uma confirmação que o
          // servidor recusaria.
          if (!simulada.liberado) {
            setErroDoMes(`Liberado a partir de ${dia(simulada.liberado_em)}.`);
            return;
          }
          setMudou(false);
          setSimulacao(simulada);
        },
        onError: (falha) => {
          if (campoDaRecusa(falha) === "competencia") setErroDoMes(textoDoErro(falha));
          else setErroGeral(textoDoErro(falha));
        },
      },
    );
  }

  async function confirmar(atual: SimulacaoDeFechamento) {
    try {
      const gravado = await fechar.mutateAsync({ competencia: atual.competencia, marca: atual.marca });
      setSimulacao(null);
      setResultado(gravado);
    } catch (falha) {
      const nova = simulacaoDesatualizada(falha);
      if (nova !== null) {
        setMudou(true);
        setSimulacao(nova);
        return;
      }
      throw falha;
    }
  }

  if (resultado !== null) return <ResultadoDoFechamentoPainel resultado={resultado} aoFechar={aoFechar} />;

  return (
    <>
      <ModalDeCadastro
        aberto={simulacao === null}
        aoFechar={aoFechar}
        titulo="Fechar competência"
        aoEnviar={simularMes}
        enviando={simular.isPending}
        erro={erroGeral}
        rotuloDeEnvio="Simular"
      >
        <Campo
          rotulo="Competência"
          type="month"
          required
          value={mes}
          onChange={(evento) => setMes(evento.target.value)}
          erro={erroDoMes}
          dica="Só fecha a partir do dia de carência do mês seguinte."
          autoFocus
        />
      </ModalDeCadastro>

      <Confirmacao
        pedido={
          simulacao === null
            ? null
            : {
                severidade: "critica",
                identidade: simulacao.marca,
                titulo: `Fechar ${competencia(simulacao.competencia)}?`,
                corpo: <ResumoDaSimulacao simulacao={simulacao} mudou={mudou} />,
                verbo: "Fechar competência",
                digitarParaLiberar: competencia(simulacao.competencia),
              }
        }
        aoCancelar={() => setSimulacao(null)}
        aoConfirmar={async () => {
          if (simulacao !== null) await confirmar(simulacao);
        }}
      />
    </>
  );
}

/** Os números da confirmação. Só aparece o que não é zero. */
function ResumoDaSimulacao({
  simulacao,
  mudou,
}: {
  simulacao: SimulacaoDeFechamento;
  mudou: boolean;
}) {
  const ajustes = simulacao.ajustes_sem_destino.reduce((soma, ajuste) => soma + ajuste.valor_centavos, 0);

  const extras: [string, string][] = [];
  if (simulacao.contratos_sem_valor > 0) {
    extras.push(["Sem valor", quantidade(simulacao.contratos_sem_valor)]);
  }
  if (simulacao.ja_faturados > 0) extras.push(["Já faturados", quantidade(simulacao.ja_faturados)]);
  if (simulacao.ajustes_sem_destino.length > 0) {
    extras.push(["Ajustes sem destino", dinheiro(ajustes)]);
  }
  if (simulacao.eventos_sem_contrato > 0) {
    extras.push(["Eventos sem contrato", quantidade(simulacao.eventos_sem_contrato)]);
  }

  return (
    <div className="flex flex-col gap-2">
      {mudou && (
        <p role="status" className="text-transito">
          Os números mudaram desde a simulação.
        </p>
      )}
      <p className="dados text-titulo text-texto">
        {simulacao.faturas === 0
          ? "Nenhuma fatura"
          : `${contagem(simulacao.faturas, "fatura", "faturas")} · ${dinheiro(simulacao.total_centavos)}`}
      </p>
      {extras.length > 0 && (
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 text-[12px]">
          {extras.map(([rotulo, valor]) => (
            <div key={rotulo} className="contents">
              <dt className="text-texto-2">{rotulo}</dt>
              <dd className="dados text-right text-texto">{valor}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/*
 * O que o fechamento gravou. Falha num contrato não desfaz os outros: ela
 * aparece aqui, e rodar o fechamento de novo completa só o que faltou — os já
 * gravados entram em "já faturados".
 */
function ResultadoDoFechamentoPainel({
  resultado,
  aoFechar,
}: {
  resultado: ResultadoDoFechamento;
  aoFechar: () => void;
}) {
  return (
    <PainelLateral
      aberto
      aoFechar={aoFechar}
      descricao={`Fechamento de ${competencia(resultado.competencia)}`}
      titulo={
        <div className="flex flex-col gap-1">
          <span className="text-titulo font-semibold text-texto">
            {`Fechamento de ${competencia(resultado.competencia)}`}
          </span>
          <span className="dados text-[12px] text-texto-2">
            {`${contagem(resultado.faturas_geradas.length, "fatura", "faturas")} · ${dinheiro(resultado.total_centavos)}`}
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        {resultado.falhas.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="rotulo text-falha">Falhas</h3>
            <ul role="alert" className="flex flex-col gap-1">
              {resultado.falhas.map((falha) => (
                <li
                  key={falha.contrato_id}
                  className="rounded-controle border border-falha/40 bg-falha/10 px-3 py-2 text-[12px] text-falha"
                >
                  <span className="dados" title={falha.contrato_id}>
                    {idCurto(falha.contrato_id)}
                  </span>{" "}
                  · {falha.motivo}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="flex flex-col gap-2">
          <h3 className="rotulo">Geradas</h3>
          {resultado.faturas_geradas.length === 0 ? (
            <p className="text-texto-2">Nenhuma fatura.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {resultado.faturas_geradas.map((fatura) => (
                <li
                  key={fatura.id}
                  className="flex items-center justify-between gap-2 rounded-controle border border-fio bg-superficie-2 px-3 py-2"
                >
                  <span className="min-w-0 truncate text-texto">{nomeDoPagador(fatura)}</span>
                  <span className="dados shrink-0 text-texto">{dinheiro(fatura.total_centavos)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {resultado.ajustes_sem_destino.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="rotulo">Ajustes sem destino</h3>
            <ul className="flex flex-col gap-1">
              {resultado.ajustes_sem_destino.map((ajuste) => (
                <li
                  key={ajuste.contrato_id}
                  className="flex items-center justify-between gap-2 rounded-controle border border-fio bg-superficie-2 px-3 py-2"
                >
                  <span className="min-w-0 truncate text-texto">{nomeDoPagador(ajuste)}</span>
                  <span className="dados shrink-0 text-falha">{dinheiro(ajuste.valor_centavos)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="grid grid-cols-2 gap-2">
          <CaixaDeCampo rotulo="Sem valor" dados>
            {quantidade(resultado.contratos_sem_valor)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Já faturados" dados>
            {quantidade(resultado.ja_faturados)}
          </CaixaDeCampo>
        </div>
      </div>
    </PainelLateral>
  );
}
